import React, { useState, useEffect } from 'react'
import { RefreshCw, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import TeamLogo from './TeamLogo'

// Fetch standings from API
async function fetchStandings(league = 'nba', forceRefresh = false) {
  const baseUrl = import.meta.env.VITE_API_URL || 'https://orbglide-analyzer.orbglide.workers.dev'
  const endpoint = forceRefresh ? '/api/standings/refresh' : '/api/standings'
  const method = forceRefresh ? 'POST' : 'GET'
  
  const res = await fetch(`${baseUrl}${endpoint}?league=${league}`, { method })
  if (!res.ok) throw new Error('Failed to fetch standings')
  return res.json()
}

function StandingRow({ team, rank, isPlayoffSpot, isPlayIn, league = 'nba' }) {
  const winPct = team.wins + team.losses > 0 
    ? (team.wins / (team.wins + team.losses)).toFixed(3).slice(1) 
    : '.000'
  
  return (
    <div 
      className={`flex items-center px-3 py-2 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        isPlayoffSpot ? 'bg-green-50/30' : isPlayIn ? 'bg-yellow-50/30' : ''
      }`}
    >
      {/* Rank */}
      <span className={`w-5 text-center text-[11px] font-semibold shrink-0 ${
        rank <= 6 ? 'text-green-600' : rank <= 10 ? 'text-yellow-600' : 'text-gray-400'
      }`}>
        {rank}
      </span>
      
      {/* Team logo */}
      <div className="w-6 shrink-0 ml-1.5">
        <TeamLogo code={team.team} name={team.teamName} league={league} size={20} />
      </div>
      
      {/* Team abbreviation */}
      <span className="text-[12px] font-medium text-gray-600 w-9 shrink-0 ml-1.5">{team.team}</span>
      
      {/* Record */}
      <div className="flex-1 text-right shrink-0 ml-1">
        <span className="text-[12px] font-medium text-gray-600">{team.wins}-{team.losses}</span>
        <span className="text-[10px] text-gray-400 ml-1">{winPct}</span>
      </div>
      
      {/* Home/Road - compact */}
      <div className="text-right text-[9px] text-gray-400 shrink-0 ml-2">
        <span>{team.homeRecord}</span>
        <span className="mx-0.5">/</span>
        <span>{team.roadRecord}</span>
      </div>
    </div>
  )
}

// NFL Division Standings Component
function NFLDivisionStandings({ conference, teams }) {
  // Group teams by division
  const divisions = {}
  const divisionOrder = conference === 'AFC' 
    ? ['EAST', 'NORTH', 'SOUTH', 'WEST'] 
    : ['EAST', 'NORTH', 'SOUTH', 'WEST']
  
  for (const team of teams) {
    const div = team.division || 'UNKNOWN'
    if (!divisions[div]) divisions[div] = []
    divisions[div].push(team)
  }
  
  // Sort each division by wins
  for (const div of Object.keys(divisions)) {
    divisions[div].sort((a, b) => b.wins - a.wins || a.losses - b.losses)
  }
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Conference header */}
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-bold text-gray-900">{conference}</h3>
      </div>
      
      {/* Divisions */}
      {divisionOrder.map(divName => {
        const divTeams = divisions[divName] || []
        if (divTeams.length === 0) return null
        
        return (
          <div key={divName}>
            {/* Division header */}
            <div className="px-3 py-1.5 bg-gray-100/50 border-b border-gray-100">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                {conference} {divName}
              </span>
            </div>
            
            {/* Division teams */}
            {divTeams.map((team, idx) => {
              const winPct = team.wins + team.losses > 0 
                ? (team.wins / (team.wins + team.losses)).toFixed(3).slice(1) 
                : '.000'
              const isDivLeader = idx === 0
              
              return (
                <div 
                  key={team.team}
                  className={`flex items-center px-2 py-2 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    isDivLeader ? 'bg-green-50/40' : ''
                  }`}
                >
                  {/* Rank indicator */}
                  <span className={`w-5 text-center text-xs font-semibold shrink-0 ${
                    isDivLeader ? 'text-green-600' : 'text-gray-400'
                  }`}>
                    {isDivLeader ? '★' : idx + 1}
                  </span>
                  
                  {/* Team logo */}
                  <div className="w-6 shrink-0 ml-1.5">
                    <TeamLogo code={team.team} name={team.teamName} league="nfl" size={20} />
                  </div>
                  
                  {/* Team abbreviation */}
                  <span className="text-[12px] font-medium text-gray-600 w-9 shrink-0 ml-1.5">{team.team}</span>
                  
                  {/* Record */}
                  <div className="flex-1 text-right shrink-0 ml-1">
                    <span className="text-[12px] font-medium text-gray-600">{team.wins}-{team.losses}</span>
                    <span className="text-[10px] text-gray-400 ml-1">{winPct}</span>
                  </div>
                  
                  {/* Home/Road - inline like NBA */}
                  <div className="text-right text-[9px] text-gray-400 shrink-0 ml-2">
                    <span>{team.homeRecord}</span>
                    <span className="mx-0.5">/</span>
                    <span>{team.roadRecord}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}
      
      {/* Legend */}
      <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex gap-4 text-[10px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="text-green-600">★</span> Division Leader
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500"></span> Playoff (Top 7)
        </span>
      </div>
    </div>
  )
}

function ConferenceStandings({ conference, teams, league = 'nba' }) {
  const isNFL = league === 'nfl'
  // NFL uses AFC/NFC, NBA uses Eastern/Western
  const conferenceLabel = isNFL ? conference : `${conference}ern Conference`
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Conference header */}
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-500">{conferenceLabel}</h3>
      </div>
      
      {/* Column headers */}
      <div className="flex items-center px-3 py-1.5 bg-gray-50/50 text-[9px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
        <span className="w-5 text-center">#</span>
        <span className="w-6 ml-1.5"></span>
        <span className="w-9 ml-1.5">Team</span>
        <span className="flex-1 text-right ml-1">Record</span>
        <span className="text-right ml-2">H/A</span>
      </div>
      
      {/* Teams */}
      <div>
        {teams.map((team, idx) => (
          <StandingRow 
            key={team.team} 
            team={team} 
            rank={idx + 1}
            isPlayoffSpot={idx < 6}
            isPlayIn={idx >= 6 && idx < 10}
            league={league}
          />
        ))}
      </div>
      
      {/* Legend */}
      <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex gap-4 text-[10px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500"></span> Playoff (1-6)
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-yellow-500"></span> Play-In (7-10)
        </span>
      </div>
    </div>
  )
}

export default function StandingsTab({ league = 'nba' }) {
  const [standings, setStandings] = useState({ east: [], west: [] })
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [emptyMessage, setEmptyMessage] = useState(null)

  // Fetch standings on mount or when league changes
  useEffect(() => {
    // Reset state when league changes to prevent shape mismatch
    setStandings(league === 'nfl' ? { afc: [], nfc: [] } : { east: [], west: [] })
    setIsLoading(true)
    setError(null)
    setEmptyMessage(null)
    fetchStandings(league)
      .then(data => {
        if (data?.error) {
          setStandings(league === 'nfl' ? { afc: [], nfc: [] } : { east: [], west: [] })
          setEmptyMessage(data.message || 'Standings are not available yet.')
          setLastUpdated(new Date())
          setIsLoading(false)
          return
        }

        // Defensive: backend should return { standings: { east, west } } or { afc, nfc }
        const nextStandings = data?.standings && !Array.isArray(data.standings)
          ? data.standings
          : (league === 'nfl' ? { afc: [], nfc: [] } : { east: [], west: [] })

        setStandings(nextStandings)
        const isEmpty = league === 'nfl'
          ? ((nextStandings.afc?.length || 0) + (nextStandings.nfc?.length || 0) === 0)
          : ((nextStandings.east?.length || 0) + (nextStandings.west?.length || 0) === 0)
        setEmptyMessage(isEmpty ? 'No standings data available yet.' : null)
        setLastUpdated(new Date())
        setIsLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setIsLoading(false)
      })
  }, [league])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      const data = await fetchStandings(league, true)
      if (data?.error) {
        setStandings(league === 'nfl' ? { afc: [], nfc: [] } : { east: [], west: [] })
        setEmptyMessage(data.message || 'Standings are not available yet.')
      } else {
        const nextStandings = data?.standings && !Array.isArray(data.standings)
          ? data.standings
          : (league === 'nfl' ? { afc: [], nfc: [] } : { east: [], west: [] })
        setStandings(nextStandings)
        const isEmpty = league === 'nfl'
          ? ((nextStandings.afc?.length || 0) + (nextStandings.nfc?.length || 0) === 0)
          : ((nextStandings.east?.length || 0) + (nextStandings.west?.length || 0) === 0)
        setEmptyMessage(isEmpty ? 'No standings data available yet.' : null)
      }
      setLastUpdated(new Date())
      setError(null)
    } catch (err) {
      setError(err.message)
    }
    setIsRefreshing(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 text-red-600 rounded-lg p-3 text-sm">
          {error}
        </div>
        <button 
          onClick={handleRefresh}
          className="mt-3 text-sm text-pink-500 hover:text-pink-600"
        >
          Try again
        </button>
      </div>
    )
  }

  const isNFL = league === 'nfl'
  const leagueLabel = isNFL ? 'NFL' : 'NBA'
  const seasonLabel = isNFL ? '2025 Season' : '2025-26 Season'

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{leagueLabel} Standings</h2>
          <p className="text-[11px] text-gray-400">
            {seasonLabel} {lastUpdated && `• Updated ${lastUpdated.toLocaleTimeString()}`}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 text-gray-500 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Standings content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {emptyMessage && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-center text-gray-500 text-sm">{emptyMessage}</div>
            <div className="mt-2 text-center">
              <button
                onClick={handleRefresh}
                className="text-sm text-pink-500 hover:text-pink-600"
              >
                Refresh
              </button>
            </div>
          </div>
        )}
        {isNFL ? (
          <>
            <NFLDivisionStandings conference="AFC" teams={standings.afc || standings.east || []} />
            <NFLDivisionStandings conference="NFC" teams={standings.nfc || standings.west || []} />
          </>
        ) : (
          <>
            <ConferenceStandings conference="East" teams={standings.east || []} league={league} />
            <ConferenceStandings conference="West" teams={standings.west || []} league={league} />
          </>
        )}
      </div>
    </div>
  )
}
